<template>
  <div>
    <component v-if="shouldRenderShell" :is="layoutComponent">
      <AdminBar v-if="showAdminBar && isAdmin"/>
      <router-view />
    </component>
    <div v-else class="flex min-h-dvh items-center justify-center">
      <ProgressSpinner size="large" animationDuration="1s" />
    </div>
  </div>
  <Drawer v-model:visible="contentModalVisible" @hide="closeContentModal" :position="smallScreenSize ? 'full' : 'right'" :block-scroll="true">
    <ContentModal />
  </Drawer>
  <Toast/>
  <PwaUpdatePrompt />

  <PiniaColadaDevtools v-if="isDev" />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onErrorCaptured, onMounted, ref } from "vue";
import pinia from "@/plugins/PiniaPlugin";

const isDev = import.meta.env.DEV;
const PiniaColadaDevtools = isDev
    ? defineAsyncComponent( () => import( '@pinia/colada-devtools' ).then( m => m.PiniaColadaDevtools ) )
    : () => null;

import { useWindowSize } from '@vueuse/core'

import PublicLayout from "@/layouts/Public.vue";
import PrivateLayout from "@/layouts/Private.vue";
import AdminBar from '@/components/layout/AdminBar.vue';
import ContentModal from "@/components/modals/ContentModal.vue";
import PwaUpdatePrompt from '@/components/layout/PwaUpdatePrompt.vue';

import { useApplicationStore } from "@/stores/Application";
import { storeToRefs } from "pinia";
import { usePwaInstall } from "@/composables/pwa-install";
import { useRoute } from "vue-router";
import * as tmi from "@/constants";
import routeManager from "@/router/route-manager";

usePwaInstall();

import browserUpdate, { Options } from 'browser-update';
import { useAuthStore } from "@/stores/Auth";
import { clientStateManager } from "@/services/error-services";
import { ConfigurationError } from "@tmi/types/errors";

const options: Options = {
  api: 2024.10,
  required: { e: -2, f: -2, o: 0, s: -1, c: -4 },
  insecure: true,
  mobile: true,
  newwindow: true,
  no_permanent_hide: true,
  shift_page_down: false,
  text: {
    msg: 'Your web browser ({brow_name}) is out of date.',
    msgmore: 'This site uses the latest technologies and thus needs a browser that is able to support such features. Update your browser for more security, speed and the best experience on this site.',
    bupdate: 'Update browser',
    bignore: 'Ignore',
    remind: 'You will be reminded in {days} days.'
  },
  test: false,
  dont_show_debuginfo: true
}

if ( typeof browserUpdate === 'undefined' ) {
  throw new Error( 'The plugin "browser-update" could not be loaded.' );
} else {
  browserUpdate( options, options.test );
}

// Content flyout/sidebar
const appStore = useApplicationStore( pinia );
const {
  contentModalVisible,
  isShellReady
} = storeToRefs( appStore );
const { isAuthenticated } = storeToRefs( useAuthStore() );
const route = useRoute();
const isAuthPage = computed( () =>
  routeManager.isSignInPath( route.path ) ||
  routeManager.isSignInAsUserName( route.name?.toString() ) ||
  routeManager.isSignUpPath( route.path ) ||
  route.path === routeManager.resetPasswordRoute() ||
  route.path === tmi.NewPasswordPath
);
// Public routes render immediately instead of waiting on the whole data layer.
//
// bootstrapShell() awaits nine store initialisations — the ticker universe, every sector, product,
// author, category and tag — and until it settled, EVERY visitor saw a full-screen spinner. That
// included anonymous readers arriving on an article, and search-engine crawlers, none of whom need
// any of it. Components that do need catalog data now show their own loading state in place
// rather than blocking the entire page behind one gate.
//
// Anything hydrating cached content still has to wait for the composition context, because
// hydrateContent() commits a non-reactive snapshot — see the stores' initialize().
const isPublicRoute = computed< boolean >( () => true === route.meta?.public );
const shouldRenderShell = computed( () => isShellReady.value || isAuthPage.value || isPublicRoute.value );
const layoutComponent = computed( () => isAuthPage.value ? PublicLayout : ( isAuthenticated.value ? PrivateLayout : PublicLayout ) );
const isAdmin = computed< boolean >( () => appStore.isAdminSession() );
const showAdminBar = computed< boolean >( () => isAdmin.value || !!( localStorage.getItem( 'is-admin' ) ) );

function closeContentModal() {
  appStore.clearSelectedContent();
}

const { width } = useWindowSize();
const smallScreenSize = computed< boolean >( () => width.value < 768 );

const configurationFault = ref< boolean >( false );
onErrorCaptured( ( err: any ) => {
  if ( !configurationFault.value ) {
    clientStateManager.queueClientError( err, {} );

    // This keeps the browser from throwing multiple errors caused by the root issue of a configuration issue
    if ( err instanceof ConfigurationError ) {
      configurationFault.value = true;
    }
  }

  // Don't allow the error to bubble up any further
  return false;
} );

onMounted( () => {
  window.addEventListener( "beforeunload", appStore.terminateWorker );
  window.addEventListener( "unhandledrejection", appStore.handleRejectedPromise );
} );

onBeforeUnmount( () => {
  window.removeEventListener( "beforeunload", appStore.terminateWorker );
  window.removeEventListener( "unhandledrejection", appStore.handleRejectedPromise );
} );

void appStore.bootstrapShell();
</script>

<style lang="scss">
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.75s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
